import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { motion } from "framer-motion";
import { CultivWordmark } from "@/components/EdenWordmark";
import { AREAS } from "@/lib/gardens";

const HERO_IMG = "https://images.unsplash.com/photo-1502082553048-f009c37129b9?w=2000&auto=format&fit=crop&q=80";
const LEAF_IMG = "https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?w=1600&auto=format&fit=crop&q=80";
const MISSION_IMG = "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1600&auto=format&fit=crop&q=80";

export default function Landing() {
    const { theme, toggle } = useTheme();
    return (
        <div className="min-h-screen bg-background text-foreground grain">
            {/* Nav */}
            <header className="absolute top-0 left-0 right-0 z-30">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8 flex items-center">
                    <Link to="/" data-testid="landing-logo">
                        <CultivWordmark size="sm" tone="cream" />
                    </Link>
                    <div className="ml-auto flex items-center gap-3">
                        <button
                            onClick={toggle}
                            className="text-[11px] uppercase tracking-[0.3em] text-[hsl(var(--eden-cream))]/70 hover:text-[hsl(var(--eden-cream))] transition-colors"
                            data-testid="landing-theme-toggle"
                        >
                            {theme === "dark" ? "Light" : "Dark"}
                        </button>
                        <Link to="/login" data-testid="landing-login-link">
                            <Button variant="ghost" className="text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-cream))]/10 hover:text-[hsl(var(--eden-cream))] text-xs uppercase tracking-[0.3em]">
                                Sign in
                            </Button>
                        </Link>
                        <Link to="/signup" data-testid="landing-signup-link">
                            <Button className="bg-[hsl(var(--eden-cream))] text-[hsl(var(--eden-forest))] hover:bg-white rounded-md text-xs uppercase tracking-[0.3em] h-10 px-5">
                                Begin
                            </Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero split */}
            <section className="relative min-h-[92vh] grid md:grid-cols-2">
                <div className="relative min-h-[50vh]">
                    <img src={HERO_IMG} alt="A sun-lit garden path" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-black/40 via-black/10 to-transparent" />
                </div>
                <div className="bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] flex items-center px-8 md:px-16 py-20 md:py-0 relative">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                        className="max-w-lg"
                    >
                        <div className="text-[10px] uppercase tracking-[0.4em] text-[hsl(var(--eden-gold))] mb-8">
                            Stewardship · Growth · Kingdom
                        </div>
                        <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] font-light">
                            You are not just living a life. You are <em className="text-[hsl(var(--eden-gold))]">cultivating</em> a garden.
                        </h1>
                        <div className="mt-8 h-px w-16 bg-[hsl(var(--eden-gold))]" />
                        <p className="mt-8 text-[hsl(var(--eden-cream))]/70 leading-relaxed max-w-md">
                            Cultiv helps you faithfully cultivate every area of the garden God has entrusted to you —
                            through a peaceful blueprint of stewardship, not the pressure of self-improvement.
                        </p>
                        <div className="mt-12 flex flex-wrap gap-3">
                            <Link to="/signup" data-testid="hero-cta-signup">
                                <Button className="bg-[hsl(var(--eden-cream))] text-[hsl(var(--eden-forest))] hover:bg-white rounded-md h-12 px-6 text-xs uppercase tracking-[0.3em]">
                                    Begin the Blueprint <ArrowRight className="ml-3 w-4 h-4" strokeWidth={1.2} />
                                </Button>
                            </Link>
                            <Link to="/login" data-testid="hero-cta-login">
                                <Button variant="outline" className="border-[hsl(var(--eden-cream))]/40 text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-cream))]/10 hover:text-[hsl(var(--eden-cream))] rounded-md h-12 px-6 text-xs uppercase tracking-[0.3em] bg-transparent">
                                    I have an account
                                </Button>
                            </Link>
                        </div>
                        <div className="mt-16 text-xs uppercase tracking-[0.35em] text-[hsl(var(--eden-cream))]/40">
                            Genesis 2:15
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Mission */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-40">
                <div className="grid md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-5">
                        <div className="text-[10px] uppercase tracking-[0.4em] text-[hsl(var(--eden-gold))] mb-6">Our mission</div>
                        <h2 className="font-serif text-4xl md:text-5xl leading-[1.05]">
                            To help people cultivate every area of life that God has entrusted to them — so His kingdom flourishes through faithful stewardship.
                        </h2>
                    </div>
                    <div className="md:col-span-6 md:col-start-7">
                        <div className="relative aspect-[4/5] rounded-md overflow-hidden hairline">
                            <img src={LEAF_IMG} alt="Fresh green leaves catching light" className="absolute inset-0 w-full h-full object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Eight areas of the garden God has entrusted to you */}
            <section className="border-t border-foreground/10">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
                    <div className="text-center mb-16">
                        <div className="eyebrow mb-4">The garden entrusted to you · eight areas</div>
                        <h2 className="font-serif text-4xl md:text-5xl leading-[1.05]">
                            Eight interconnected areas of one entrusted life.
                        </h2>
                        <p className="mt-6 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                            Heart, Body, Mind, Home, Relationships, Work &amp; Calling, Rest, and Kingdom are not
                            separate plots. They are the interwoven soil of the garden God has entrusted to you —
                            cultivated together.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 max-w-4xl mx-auto">
                        {AREAS.map((g) => {
                            const Icon = g.Icon;
                            return (
                                <div key={g.key} className="flex flex-col items-center text-center">
                                    <div className="w-20 h-20 rounded-full hairline flex items-center justify-center">
                                        <Icon className="w-6 h-6 text-[hsl(var(--eden-forest))] dark:text-[hsl(var(--eden-cream))]" strokeWidth={1.2} />
                                    </div>
                                    <div className="mt-4 text-[11px] uppercase tracking-[0.3em]">{g.label}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Faithfulness pull-quote */}
            <section className="border-t border-foreground/10">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-2 gap-12 items-center">
                    <div className="relative aspect-[4/5] rounded-md overflow-hidden hairline">
                        <img src={MISSION_IMG} alt="Deep botanical greens" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/30" />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.4em] text-[hsl(var(--eden-gold))] mb-6">A quiet truth</div>
                        <blockquote className="font-serif text-4xl md:text-6xl leading-[1.05]">
                            Faithfulness in the small cultivates fruitfulness in the great.
                        </blockquote>
                    </div>
                </div>
            </section>

            {/* Editorial garden strip */}
            <section>
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
                    <div className="eyebrow mb-8">Each area · everyday stewardship</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {AREAS.map((g) => (
                            <div key={g.key} className="group">
                                <div className="relative aspect-[3/4] rounded-md overflow-hidden hairline">
                                    <img src={g.image} alt={g.label} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                    <div className="absolute inset-0 p-4 flex flex-col justify-end text-white">
                                        <div className="text-[10px] uppercase tracking-[0.3em]">{g.label}</div>
                                        <div className="font-serif text-lg mt-1 leading-snug">{g.virtue}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="border-t border-foreground/10 bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
                    <div className="text-[10px] uppercase tracking-[0.4em] text-[hsl(var(--eden-gold))] mb-6">Begin the Blueprint</div>
                    <h3 className="font-serif text-4xl md:text-6xl leading-[1.05] max-w-3xl mx-auto">
                        Your garden is waiting to be tended.
                    </h3>
                    <p className="mt-6 text-[hsl(var(--eden-cream))]/70 max-w-xl mx-auto leading-relaxed">
                        Ten unhurried minutes. A personal blueprint composed for you. A gentler way forward.
                    </p>
                    <div className="mt-12 flex justify-center gap-3">
                        <Link to="/signup" data-testid="footer-cta">
                            <Button className="bg-[hsl(var(--eden-cream))] text-[hsl(var(--eden-forest))] hover:bg-white rounded-md h-12 px-8 text-xs uppercase tracking-[0.3em]">
                                Cultivate what God has entrusted
                            </Button>
                        </Link>
                    </div>
                    <div className="mt-24 text-[10px] uppercase tracking-[0.4em] text-[hsl(var(--eden-cream))]/50">
                        Formed by Him · Filled by Him · Established by Him
                    </div>
                    <div className="mt-6">
                        <Link
                            to="/how-we-speak"
                            data-testid="landing-how-we-speak-link"
                            className="text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--eden-cream))]/60 hover:text-[hsl(var(--eden-gold))] transition-colors"
                        >
                            Read how Cultiv speaks
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
